import React from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Search, X } from 'lucide-react';

/**
 * Boîte de recherche compacte pour filtrer les lignes des tableaux comptables.
 * Filtre par libellé, description, numéro de facture, catégorie, annonceur, etc.
 *
 * Props :
 *  - value: string (terme recherché)
 *  - onChange: (val) => void
 *  - placeholder: texte d'aide
 *  - testIdPrefix: préfixe pour data-testid
 *  - color: palette (indigo/blue/green/red/amber)
 */
const PALETTES = {
  indigo: 'border-indigo-300 focus-visible:ring-indigo-500',
  blue: 'border-blue-300 focus-visible:ring-blue-500',
  green: 'border-green-300 focus-visible:ring-green-500',
  red: 'border-red-300 focus-visible:ring-red-500',
  amber: 'border-amber-300 focus-visible:ring-amber-500',
};

export default function SearchBox({
  value = '',
  onChange,
  placeholder = 'Rechercher (libellé, n° facture, client…)',
  testIdPrefix = 'search',
  color = 'indigo',
  className = '',
}) {
  const palette = PALETTES[color] || PALETTES.indigo;
  return (
    <div className={`relative inline-flex items-center ${className}`} data-testid={`${testIdPrefix}-container`}>
      <Search className="absolute left-2 h-4 w-4 text-gray-400 pointer-events-none" />
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        className={`h-8 pl-8 pr-8 w-64 text-xs bg-white text-gray-900 ${palette}`}
        data-testid={`${testIdPrefix}-input`}
      />
      {value && (
        <Button
          size="sm"
          variant="ghost"
          type="button"
          onClick={() => onChange?.('')}
          className="absolute right-1 h-6 w-6 p-0 text-gray-500 hover:text-red-600"
          data-testid={`${testIdPrefix}-clear`}
          title="Effacer la recherche"
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

/**
 * Filtre une liste de lignes comptables par terme de recherche.
 * Cherche dans les champs : libelle, description, categorie, annonceur, numero,
 * source_facture_numero, type, cote.
 */
export function applySearch(rows, term) {
  if (!Array.isArray(rows)) return [];
  const q = (term || '').trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => {
    if (!r) return false;
    const haystack = [
      r.libelle, r.description, r.categorie, r.annonceur, r.numero,
      r.source_facture_numero, r.client, r.fournisseur, r.objet,
      r.type, r.cote,
    ]
      .filter(Boolean)
      .map((v) => String(v).toLowerCase())
      .join(' ');
    return haystack.includes(q);
  });
}
